package com.example.socialmediaapp.data.models

import kotlinx.serialization.Serializable

// ============================================
// CORE DATA MODELS
// ============================================

@Serializable
data class User(
    val id: String,
    val username: String,
    val email: String,
    val bio: String? = null,
    val avatarUrl: String? = null
)

@Serializable
data class Message(
    val id: String,
    val chatId: String,
    val senderId: String,
    val senderName: String? = null,
    val content: String,
    val timestamp: Long,
    val readAt: Long? = null,
    val deliveredAt: Long? = null,
    val editedAt: Long? = null,
    val deletedAt: Long? = null,
    val mediaUrls: List<String> = emptyList(),
    val forwardedFrom: String? = null,
    val replyToId: String? = null
)

@Serializable
data class Chat(
    val id: String,
    val name: String,
    val type: ChatType,
    val participants: List<String>,
    val lastMessage: Message? = null,
    val unreadCount: Int = 0,
    val isPinned: Boolean = false,
    val isMuted: Boolean = false,
    val isArchived: Boolean = false
)

@Serializable
enum class ChatType {
    DIRECT,
    GROUP,
    CHANNEL
}

// ============================================
// SOCIAL / FEED MODELS
// ============================================

@Serializable
data class Post(
    val id: String,
    val authorId: String,
    val authorName: String,
    val authorAvatar: String? = null,
    val content: String,
    val imageUrls: List<String> = emptyList(),
    val timestamp: Long,
    val likeCount: Int = 0,
    val commentCount: Int = 0,
    val shareCount: Int = 0,
    val isLikedByMe: Boolean = false
)

@Serializable
data class Comment(
    val id: String,
    val postId: String,
    val authorId: String,
    val authorName: String,
    val authorAvatar: String? = null,
    val content: String,
    val timestamp: Long,
    val likeCount: Int = 0,
    val isLikedByMe: Boolean = false
)

// ============================================
// SHORTS / VIDEO MODELS
// ============================================

@Serializable
data class Short(
    val id: String,
    val username: String,
    val thumbnailUrl: String,
    val videoUrl: String,
    val caption: String,
    val tags: List<String> = emptyList(),
    val audioName: String = "Original Audio",
    val likeCount: Int = 0,
    val commentCount: Int = 0,
    val shareCount: Int = 0
)

// ============================================
// STORIES MODELS
// ============================================

@Serializable
data class Story(
    val id: String,
    val userId: String,
    val mediaUrl: String,
    val thumbnailUrl: String? = null,
    val isVideo: Boolean = false,
    val timestamp: Long,
    val expiresAt: Long = timestamp + (24 * 60 * 60 * 1000)
)

@Serializable
data class StoryGroup(
    val userId: String,
    val username: String,
    val userAvatar: String? = null,
    val stories: List<Story>,
    val hasUnseenStories: Boolean = true
) {
    val latestStory: Story get() = stories.last()
}

// ============================================
// LIVE STREAMING MODELS
// ============================================

@Serializable
data class LiveComment(
    val id: String,
    val userId: String,
    val username: String,
    val message: String,
    val timestamp: Long
)

// ============================================
// MEDIA MODELS
// ============================================

@Serializable
data class Sticker(
    val id: String,
    val name: String,
    val url: String,
    val category: String
)

// ============================================
// NOTIFICATION MODELS
// ============================================

@Serializable
data class Notification(
    val id: String,
    val type: NotificationType,
    val title: String,
    val content: String,
    val timestamp: Long,
    val isRead: Boolean = false,
    val relatedId: String? = null
)

@Serializable
enum class NotificationType {
    MESSAGE,
    MENTION,
    LIKE,
    FOLLOW,
    SUBSCRIPTION
}

// ============================================
// MARKETPLACE MODELS
// ============================================

@Serializable
data class Product(
    val id: String,
    val title: String,
    val description: String,
    val price: Double,
    val currency: String = "USD",
    val imageUrls: List<String> = emptyList(),
    val category: String,
    val sellerId: String,
    val sellerName: String,
    val timestamp: Long
)

// ============================================
// PRESENCE MODELS
// ============================================

@Serializable
data class UserPresence(
    val userId: String,
    val status: PresenceStatus,
    val lastSeen: Long = 0L
)

@Serializable
enum class PresenceStatus {
    ONLINE,
    AWAY,
    OFFLINE
}

// ============================================
// UI STATE
// ============================================

sealed class UiState<out T> {
    object Idle : UiState<Nothing>()
    object Loading : UiState<Nothing>()
    data class Success<T>(val data: T) : UiState<T>()
    data class Error(val message: String, val error: Throwable? = null) : UiState<Nothing>()
}

// ============================================
// TAGGING MODELS
// ============================================

@Serializable
data class Tag(
    val id: String,
    val name: String,
    val icon: String,
    val category: String,
    val color: String,
    val postCount: Int = 0,
    val followerCount: Int = 0
)

// ============================================
// BUSINESS PROFILE MODELS
// ============================================

@Serializable
data class BusinessProfile(
    val userId: String,
    val businessName: String,
    val tagline: String? = null,
    val description: String? = null,
    val logoUrl: String? = null,
    val bannerUrl: String? = null,
    val websiteUrl: String? = null,
    val businessEmail: String? = null,
    val businessPhone: String? = null,
    val headquartersCity: String? = null,
    val headquartersCountry: String? = null,
    val industry: String? = null,
    val isVerified: Boolean = false,
    val verificationBadgeType: String = "standard",
    val followerCount: Int = 0,
    val reviewCount: Int = 0,
    val avgRating: Double? = null,
    val tags: List<Tag> = emptyList(),
    val galleryImages: List<String> = emptyList(),
    val offerings: List<BusinessOffering> = emptyList(),
    val team: List<TeamMember> = emptyList(),
    val linkedinUrl: String? = null,
    val twitterUrl: String? = null,
    val instagramUrl: String? = null
)

@Serializable
data class BusinessOffering(
    val id: String,
    val type: String,
    val name: String,
    val description: String? = null,
    val priceMin: Double? = null,
    val priceMax: Double? = null,
    val currency: String = "USD",
    val imageUrl: String? = null,
    val isFeatured: Boolean = false
)

@Serializable
data class TeamMember(
    val id: String,
    val userId: String,
    val username: String,
    val avatarUrl: String? = null,
    val role: String,
    val jobTitle: String? = null,
    val department: String? = null
)

// ============================================
// VALIDATION
// ============================================

sealed class ValidationResult {
    object Success : ValidationResult()
    data class Error(val message: String) : ValidationResult()
}
